import { useState, useEffect, useRef, type MutableRefObject, type RefObject } from "react";
import router, { useRouter } from "next/router";
import useSWR, { mutate } from "swr";
import { snapshot } from "valtio";
import debounce from "lodash.debounce";
import useSWRImmutable from 'swr/immutable';

import { Logger } from "common/middleware/logger";
import {
  LanguageType,
  DealDto,
  UserType,
} from "common/middleware/api.services";
import {
  getExistingPurchaseOffer,
  getProperties,
  getProperty,
  getProjects,
  getProject,
  getPurchaseOfferWithKey,
  getUser,
  getUsers,
} from "common/middleware/api";
import {
  getClient,
  getClientContacts,
  getClientNotes,
  getClientPurchaseOffers,
  getClients
} from "common/middleware/api.clients";
import { getLoggedInUser } from "common/middleware/api.auth";
import store from "common/middleware/store";
import { getBuilders } from "common/middleware/api.builders";
import { generateProjectEmailCopy } from "common/middleware/api.projects";
import { getPropertyDocuments, getPropertyImages } from "common/middleware/api.properties";

import { getAuthRedirectUrl } from "common/utils/utils.auth";

const LOGGED_IN_USER_KEY = "/api/loggedInUser";
const USERS_KEY = "/api/users";
const UNIT_KEY = "/api/unit";
const PURCHASE_OFFER_KEY = "/api/purchaseOffer";
const CREATE_PURCHASE_OFFER_KEY = "/api/createPurchaseOffer";
const PROPERTIES_KEY = "/api/properties";
const PROJECTS_KEY = "/api/projects";
const CLIENTS_KEY = "/api/clients";
const CLIENTS_NOTES_KEY = "/api/clients/notes";
const CLIENTS_PURCHASE_OFFERS_KEY = "/api/clients/purchaseOffers"
const CLIENTS_CONTACTS_KEY = "/api/clients/contacts"
const OPPORTUNITIES_KEY = "/api/opportunities";
const OPPORTUNITY_NOTES_KEY = "/api/opportunities/notes"
const BUILDERS_KEY = "/api/v1/builders";
const PROJECT_EMAIL_COPY_KEY = "/api/emailCopy";
const BROKER_ALLOCATED_PROPERTIES_KEY = "/api/broker/allocatedProperties";
const PROPERTY_DOCUMENTS_KEY = "/api/property/documents";
const PROPERTY_IMAGES_KEY = "/api/property/images";

export const clearPurchaseOffersCache = async () => {
  await mutate(PURCHASE_OFFER_KEY, undefined, { revalidate: false })
}

export const mutateBrokerAllocatedProperties = (
  brokerId: string,
  searchString?: string,
  page?: string,
  pageSize?: string,
  acceptOrDenyCommissionBatchCommandId?: string,
  filterByStatus?: any[],
  getAllEntities?: boolean,
) => {
  mutate([
    BROKER_ALLOCATED_PROPERTIES_KEY,
    brokerId,
    searchString,
    page,
    pageSize,
    acceptOrDenyCommissionBatchCommandId,
    filterByStatus,
    getAllEntities,
  ]);
}

export const mutateLoggedInUser = async (clearCache?: boolean) => {
  clearCache
    ? await mutate(LOGGED_IN_USER_KEY, undefined, { revalidate: false })
    : await mutate(LOGGED_IN_USER_KEY);
};

export const mutatePurchaseOffer = async (purchaseOffer?: DealDto) => {
  if (purchaseOffer) {
    Logger.debug("Local mutating purchase offer");
    await mutate([PURCHASE_OFFER_KEY], { ...purchaseOffer }, false);
    await mutate([CREATE_PURCHASE_OFFER_KEY], { ...purchaseOffer }, false);
  } else {
    await mutate([PURCHASE_OFFER_KEY]);
    await mutate([CREATE_PURCHASE_OFFER_KEY]);
  }
};

export const mutatePurchaseOfferWithId = (purchaseOfferId: string) => {
  mutate([PURCHASE_OFFER_KEY, purchaseOfferId]);
};

export const mutateClients = (
  page: string,
  pageSize: string,
  projectId: string,
  searchString: string,
) => {
  mutate([
    CLIENTS_KEY,
    page,
    pageSize,
    projectId,
    searchString,
  ]);
}

export const mutateClient = (
  clientId: number
) => {
  mutate([CLIENTS_KEY, clientId]);
}

export const mutateClientNotes = (
  clientId: number,
  filterByProjectId: string,
) => {
  mutate([CLIENTS_NOTES_KEY, clientId, filterByProjectId]);
};

export const mutateOpportunity = (opportunityId: string) => {
  mutate([OPPORTUNITIES_KEY, opportunityId]);
};

export const mutateOpportunityNotes = (
  opportunityId: string,
  projectId: string,
) => {
  mutate([OPPORTUNITY_NOTES_KEY, opportunityId, projectId]);
};

export const mutateProject = (projectId: string) => {
  mutate([PROJECTS_KEY, projectId]);
}

export const mutateProperties = (
  projectId?: string,
  phaseId?: string,
  buildingId?: string,
  builderId?: number,
) => {
  mutate([PROPERTIES_KEY, projectId, phaseId, buildingId, builderId]);
}

export const mutateUnit = async (id: number, getDraft: boolean = false) => {
  await mutate([UNIT_KEY, id, getDraft]);
}

export const mutatePropertyImages = async (propertyId: number, getDraft: boolean = false) => {
  await mutate([
    PROPERTY_IMAGES_KEY,
    propertyId,
    getDraft
  ]);
};

export const usePropertyImages = (
  propertyId: number,
  {
    shouldFetch = true,
    draft = false
  } : {
    shouldFetch?: boolean,
    draft?: boolean
  } = {}
) => {
  const { data, error, mutate } = useSWR(
    shouldFetch && propertyId
      ? [
          PROPERTY_IMAGES_KEY,
          propertyId,
          draft
        ]
      : null,
    getPropertyImages
  );

  return {
    images: data,
    isLoading: Boolean(propertyId) && shouldFetch && !error && data === undefined,
    isError: error,
    mutatePropertyImages: mutate,
  };
}

export const usePropertyDocuments = (propertyId: number, opt?: {
  filterOnSupplementaryDocumentName?: string,
  getPrivate?: boolean,
  shouldFetch?: boolean,
  getDraft?: boolean,
}) => {
  const shouldFetch = opt?.shouldFetch !== false;

  const { data, error, mutate } = useSWR(
    propertyId && shouldFetch
      ? [
          PROPERTY_DOCUMENTS_KEY,
          propertyId,
          opt?.getDraft,
          opt?.filterOnSupplementaryDocumentName,
          opt?.getPrivate,
        ]
      : null,
    getPropertyDocuments
  );

  return {
    documents: data,
    isLoading: shouldFetch && Boolean(propertyId) && !error && !data,
    isError: error,
    mutatePropertyDocs: mutate,
  };
}

export const useProperties = (opt?: {
  projectId?: string,
  phaseId?: string,
  buildingId?: string,
  builderId?: number,
  shouldFetch?: boolean,
}) => {
  const shouldFetch = opt?.shouldFetch !== false;

  const { data, error } = useSWR(
    shouldFetch
      ? [
          PROPERTIES_KEY,
          opt?.projectId,
          opt?.phaseId,
          opt?.buildingId,
          opt?.builderId,
        ]
      : null,
    getProperties
  );

  return {
    properties: data,
    isLoading: shouldFetch && !error && !data,
    isError: error,
  };
};

export const useLoggedInUser = (opt?: {
  redirectIfNoUser?: boolean,
  shouldFetch?: boolean,
  refreshInterval?: number
}) => {
  const shouldFetch = opt?.shouldFetch !== false;

  const { data, error, mutate } = useSWR(
    shouldFetch && snapshot(store).isLoggedIn
      ? LOGGED_IN_USER_KEY
      : null,
    getLoggedInUser,
    { refreshInterval: opt?.refreshInterval }
  );

  if (opt?.redirectIfNoUser && !snapshot(store).isLoggedIn) {
    if (typeof window !== "undefined") {
      window.location.href = getAuthRedirectUrl();
    }
  }

  return {
    user: data,
    isLoading: shouldFetch && !error && !data,
    isError: error,
    mutateLoggedInUser: mutate,
  };
};

export const useUser = (userId: string) => {
  const { data, error } = useSWR(
    userId ? [USERS_KEY, userId] : null,
    getUser
  );

  return {
    user: data,
    isLoading: Boolean(userId) && !error && !data,
    isError: error,
  };
};

export const useBrokers = (
  searchString?: string,
  page?: string,
  pageSize?: string
) => {
  const { data, error } = useSWR(
    [
      USERS_KEY,
      UserType.Broker,
      searchString,
      page,
      pageSize
    ],
    getUsers
  );

  return {
    brokers: data,
    isLoading: !error && !data,
    isError: error,
  };
};

export const useUnit = (
  unitId: number,
  opt?: {
    shouldFetch?: boolean,
    draft?: boolean
  },
) => {
  const shouldFetch = opt?.shouldFetch !== false;
  const getDraft = opt?.draft === true;

  const { data, error, mutate } = useSWR(
    unitId && shouldFetch
      ? [
          UNIT_KEY,
          unitId,
          getDraft
        ]
      : null,
    getProperty
  );

  return {
    unit: data,
    isUnitLoading: shouldFetch && Boolean(unitId) && !error && data === undefined,
    isError: error,
    mutateProperty: mutate,
  };
};

export const useExistingPurchaseOffer = (refreshInterval: number = 0) => {
  const { data, error, mutate } = useSWR(
    [PURCHASE_OFFER_KEY],
    getExistingPurchaseOffer,
    { refreshInterval: refreshInterval }
  );

  useEffect(() => {
    if (error?.status === 404) {
      store.purchaseOfferId = undefined;
      location.href = process.env.NEXT_PUBLIC_BUYER_URL;
      return;
    }

    if (data?.isUnitSoldToOtherDeal) {
      router.push("/unit-sold");
      return;
    }

    if (data?.isUnitReservedToOtherDeal) {
      router.push("/unit-reserved");
      return;
    }
  }, [data, error]);

  return {
    purchaseOffer: data,
    isLoading: !error && data === undefined,
    error: error,
    mutatePurchaseOffer: mutate,
  };
};

export const useProjects = (opt?: {
  builderId?: number,
  shouldFetch?: boolean,
}) => {
  const shouldFetch = opt?.shouldFetch !== false;

  const { data, error } = useSWR(
    shouldFetch ? (
      opt?.builderId
        ? [PROJECTS_KEY, opt.builderId]
        : PROJECTS_KEY
    ) : null,
    getProjects
  );

  return {
    projects: data,
    isProjectsLoading: shouldFetch && !error && !data,
    isError: error,
  };
};

export const useCurrentProject = (projectId: string) => {
  const { data, error } = useSWR(
    projectId ? [PROJECTS_KEY, projectId] : null,
    getProject
  );

  return {
    project: data,
    isProjectLoading: !error && !data,
    isError: error,
  };
};

export const useClients = (
  page?: string,
  pageSize?: string,
  projectId?: string,
  searchString?: string,
  opt?: { shouldFetch?: boolean },
) => {
  const shouldFetch = opt?.shouldFetch !== false;

  const { data, error } = useSWR(
    shouldFetch
     ? [
          CLIENTS_KEY,
          page,
          pageSize,
          projectId,
          searchString
        ]
      : null,
    getClients
  );

  return {
    clients: data,
    isClientsLoading: shouldFetch && !error && !data,
    isError: error,
  };
};

export const useClient = (
  clientId: number,
  opt?: { shouldFetch?: boolean },
) => {
  const shouldFetch = opt?.shouldFetch !== false;

  const { data, error } = useSWR(
    shouldFetch ? [CLIENTS_KEY, clientId] : null,
    getClient
  );

  return {
    client: data,
    isLoading: shouldFetch && !error && !data,
    isError: error,
  };
};

export const useClientNotes = (
  clientId: number,
  filterByProjectId: string,
) => {
  const { data, error } = useSWR(
    [CLIENTS_NOTES_KEY, clientId, filterByProjectId],
    getClientNotes
  );

  return {
    notes: data,
    isLoading: !error && !data,
    isError: error,
  }
}

export const useClientPurchaseOffers = (
  clientId: number,
  filterByProjectId?: number[],
  page?: string,
  pageSize?: string,
) => {
  const { data, error } = useSWR(
    [CLIENTS_PURCHASE_OFFERS_KEY, clientId, filterByProjectId, page, pageSize],
    getClientPurchaseOffers
  );

  return {
    offers: data,
    isLoading: !error && !data,
    isError: error,
  }
}

export const useClientContacts = (
  clientId: number,
  page?: string,
  pageSize?: string,
) => {
  const { data, error } = useSWR(
    [CLIENTS_CONTACTS_KEY, clientId, page, pageSize],
    getClientContacts
  );

  return {
    contacts: data,
    isLoading: !error && !data,
    isError: error,
  }
}

export const usePurchaseOffer = (purchaseOfferId: string, opt?: {
  shouldFetch?: boolean,
}) => {
  const shouldFetch = opt?.shouldFetch !== false;

  const { data, error, mutate } = useSWR(
    shouldFetch && purchaseOfferId
      ? [PURCHASE_OFFER_KEY, purchaseOfferId]
      : null,
    getPurchaseOfferWithKey
  );

  return {
    purchaseOffer: data,
    isLoading: shouldFetch && Boolean(purchaseOfferId) && !error && !data,
    isError: error,
    mutatePurchaseOffer: mutate,
  };
};

export const useBuilders = (
  page?: string,
  searchString?: string,
  pageSize?: string,
) => {
  const { data, error } = useSWR(
      [BUILDERS_KEY, page, searchString, pageSize],
    getBuilders
  );

  return {
    builders: data,
    isLoading: !error && !data,
    isError: error,
  };
};

export const useProjectEmailCopy = (
  projectId: number,
  noteType: string,
  requestedCulture: LanguageType,
  clientsName: string,
) => {
  const { data, error } = useSWRImmutable(
    [
      PROJECT_EMAIL_COPY_KEY,
      projectId,
      noteType,
      requestedCulture,
      clientsName,
    ],
    generateProjectEmailCopy,
  );

  return {
    copy: data,
    isLoading: !error && !data,
    isError: error,
  };
}

export const useDebouncedCallback = (
  callback: (any) => void,
  waitTime: number
) => debounce(callback, waitTime);

export const useWindowResize = () => {
  const [dimension, setDimension] = useState([0, 0]);

  const resizeHandler = useDebouncedCallback(() => {
    setDimension([window.innerWidth, window.innerHeight]);
  }, 50);

  useEffect(() => {
    setDimension([window.innerWidth, window.innerHeight]);

    window.addEventListener("resize", resizeHandler);
    return () => {
      window.removeEventListener("resize", resizeHandler);
    };
  }, []);
  return dimension;
};

export const useRefDimensions = (
  ref: RefObject<HTMLDivElement>
): { width: number; height: number } => {
  const [dimensions, setDimensions] = useState({ width: 1, height: 2 });
  const [windowWidth, windowHeight] = useWindowResize();

  useEffect(() => {
    if (ref && ref.current) {
      const { current } = ref;
      const boundingRect = current.getBoundingClientRect();
      const { width, height } = boundingRect;
      setDimensions({ width: Math.round(width), height: Math.round(height) });
    }
  }, [ref, windowWidth, windowHeight]);
  return dimensions;
};

export const useTimer = (seconds) => {
  const [timer, setTimer] = useState(seconds);
  const [isActive, setIsActive] = useState(false);

  const reset = () => {
    setTimer(seconds);
    setIsActive(false);
  };

  useEffect(() => {
    let interval = null;
    if (isActive) {
      if (timer <= 0) {
        reset();
        return () => clearInterval(interval);
      }
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else if (!isActive && timer !== 0) {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [isActive, timer]);

  return {
    timer,
    isActive,
    setIsActive,
  };
};

export const useClickOutside = (
  ref: MutableRefObject<HTMLElement | null> | MutableRefObject<HTMLElement | null>[],
  callback: () => void,
) => {
  const refRef = useRef(ref);
  const callbackRef = useRef(callback);

  refRef.current = ref;
  callbackRef.current = callback;

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const r = refRef.current;
      const shouldRun = Array.isArray(r)
        ? !r.some(refItem => refItem.current && refItem.current.contains(e.target as Node))
        : Boolean(r.current && !r.current.contains(e.target as Node));
      if (shouldRun) {
        callbackRef.current();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
};

export const useRouteChange = () => {
  const [routeChanging, setRouteChanging] = useState(false);

  const handleRouteChangeComplete = () => setRouteChanging(false);
  const handleRouteChangeStart = () => setRouteChanging(true);
  const handleRouterChangeError = () => setRouteChanging(false);

  useEffect(() => {
    router.events.on('routeChangeComplete', handleRouteChangeComplete);
    router.events.on('routeChangeStart', handleRouteChangeStart);
    router.events.on('routeChangeError', handleRouterChangeError);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
      router.events.off('routeChangeStart', handleRouteChangeStart);
      router.events.off('routeChangeError', handleRouterChangeError);
    };
  }, [router]);

  return { routeChanging };
};

export { useDirectoryListings } from "./hooks/directoryListings.hooks";

export const useAction = () => {
  const { query, isReady } = useRouter();

  const [action, setAction] = useState<
    "share"
    | "reviewSharedProperty"
  >(undefined);

  useEffect(() => {
    if (!isReady) return;

    if (
      query.action === "share"
      && query.propertyId
    ) {
      setAction("share");
      return;
    }

    if (
      query.action === "reviewSharedProperty"
      && query.propertyId
    ) {
      setAction("reviewSharedProperty");
      return;
    }

    setAction(undefined);
  }, [query, isReady]);

  return action;
}
